/**
 * composer-tools-sheet.tsx — Tiện ích của ô soạn tin trên ĐIỆN THOẠI (chat chi tiết), gồm:
 *   - `ComposerToolNode`   : một tiện ích ở dạng nút tròn (thanh công cụ) hoặc ô (hộp Tiện ích) — nối với đúng hành động / popup.
 *   - `MobileComposerToolbar`: thanh công cụ gọn kiểu ô chat của Claude mobile — nút "+" (mở hộp Tiện ích), vài tiện ích ĐƯỢC GHIM, nút gửi ở cuối.
 *   - `ComposerToolsSheet` : hộp trượt từ đáy — lưới MỌI tiện ích (chạm để dùng) và chế độ "Sắp xếp" (chọn tiện ích ghim, tối đa MAX_PINNED, kéo-thả /
 *     mũi tên đổi thứ tự). Lựa chọn lưu THEO TÀI KHOẢN, đồng bộ mọi thiết bị (hooks/use-composer-tools.ts).
 * Logic thuần: lib/composer-tools.ts. Máy tính không dùng — vẫn giữ thanh công cụ đầy đủ như cũ.
 */
import { useState, type ReactNode } from 'react'
import { ArrowDown, ArrowUp, Check, GripVertical, Plus, RotateCcw, SlidersHorizontal } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { BottomSheet, BottomSheetContent } from '@/components/ui/bottom-sheet'
import { ComposerToolTrigger, type ToolTriggerMode } from '@/components/shared/composer-tool-trigger'
import { cn } from '@/lib/utils'
import {
  COMPOSER_TOOLS, DEFAULT_PINNED, MAX_PINNED, editorOrder, isDefaultPinned, moveTool, toggleTool, toolDef,
  type ComposerToolId,
} from '@/lib/composer-tools'
import { QuickMessagesPopover } from './quick-messages-popover'
import { StickerPicker } from './sticker-picker'
import { ProductPickerButton } from './product-picker'
import { CreateOrderButton } from './create-order-dialog'
import { useDragReorder } from './mobile-segments-sheet'
import type { QuickReplyContext } from '@/pages/quick-replies/quick-reply-vars'

/** Hành động của các tiện ích không tự chứa popup (chat-panel giữ trạng thái hộp thoại / ô chọn tệp). */
export interface ComposerToolActions {
  image: () => void
  file: () => void
  library: () => void
  card: () => void
  bank: () => void
  link: () => void
  reminder: () => void
  poll: () => void
}

/** Ngữ cảnh hội thoại cần cho các tiện ích. */
export interface ComposerToolContext {
  convId: string
  accountId?: string | null
  contact?: QuickReplyContext['contact']
  contactId?: string | null
  /** Chỉ chế độ "Trả lời" mới gửi được sản phẩm (tin đi thẳng ra kênh khách). */
  canSendProduct: boolean
  uploading: boolean
  onQuickPick: (text: string) => void
  actions: ComposerToolActions
}

const ACTION_IDS: ComposerToolId[] = ['image', 'file', 'library', 'card', 'bank', 'link', 'reminder', 'poll']

/** Một tiện ích ở dạng `mode`. `after` chạy sau khi bấm tiện ích dạng hành động (vd đóng hộp Tiện ích). Tiện ích chưa dùng được trả về null. */
export function ComposerToolNode({ id, mode, ctx, after }: { id: ComposerToolId; mode: ToolTriggerMode; ctx: ComposerToolContext; after?: () => void }) {
  const { t } = useTranslation('convChat')
  const def = toolDef(id)
  if (!def) return null
  switch (id) {
    case 'sticker':
      return <StickerPicker convId={ctx.convId} mode={mode} />
    case 'quick':
      return <QuickMessagesPopover accountId={ctx.accountId} contact={ctx.contact} onPick={ctx.onQuickPick} mode={mode} />
    case 'product':
      return <ProductPickerButton convId={ctx.convId} mode={mode} disabled={!ctx.canSendProduct} disabledHint={t('composerTools.productReplyOnly')} />
    case 'order':
      return <CreateOrderButton convId={ctx.convId} contactId={ctx.contactId} mode={mode} />
    default:
      if (!ACTION_IDS.includes(id)) return null
      return (
        <ComposerToolTrigger
          mode={mode}
          icon={def.icon}
          label={def.label}
          loading={id === 'image' && ctx.uploading}
          disabled={(id === 'image' || id === 'file') && ctx.uploading}
          onClick={() => {
            ctx.actions[id as keyof ComposerToolActions]()
            after?.()
          }}
        />
      )
  }
}

/** Thanh công cụ gọn: [+ Tiện ích] [tiện ích ghim…] ………… [nút gửi]. */
export function MobileComposerToolbar({ pinned, onPinnedChange, ctx, sendButton }: {
  pinned: ComposerToolId[]
  onPinnedChange: (next: ComposerToolId[]) => void
  ctx: ComposerToolContext
  sendButton: ReactNode
}) {
  const { t } = useTranslation('convChat')
  const [open, setOpen] = useState(false)
  return (
    <div className="flex items-center gap-0.5 px-1.5">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('composerTools.title')}
        aria-haspopup="dialog"
        className="mr-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-foreground transition-colors active:bg-muted"
      >
        <Plus className="h-5 w-5" />
      </button>
      {pinned.map((id) => <ComposerToolNode key={id} id={id} mode="icon" ctx={ctx} />)}
      <div className="ml-auto pl-1">{sendButton}</div>
      <ComposerToolsSheet open={open} onOpenChange={setOpen} pinned={pinned} onPinnedChange={onPinnedChange} ctx={ctx} />
    </div>
  )
}

const GROUPS: { key: 'send' | 'tool'; titleKey: string }[] = [
  { key: 'send', titleKey: 'composerTools.groupSend' },
  { key: 'tool', titleKey: 'composerTools.groupTool' },
]

export function ComposerToolsSheet({ open, onOpenChange, pinned, onPinnedChange, ctx }: {
  open: boolean
  onOpenChange: (v: boolean) => void
  pinned: ComposerToolId[]
  onPinnedChange: (next: ComposerToolId[]) => void
  ctx: ComposerToolContext
}) {
  const { t } = useTranslation('convChat')
  const [editing, setEditing] = useState(false)
  const close = (v: boolean) => {
    if (!v) setEditing(false)
    onOpenChange(v)
  }
  return (
    <BottomSheet open={open} onOpenChange={close}>
      <BottomSheetContent title={t('composerTools.title')} className={editing ? 'h-[85dvh]' : undefined}>
        {editing ? (
          <ToolsEditor pinned={pinned} onChange={onPinnedChange} onDone={() => setEditing(false)} />
        ) : (
          <div className="space-y-4 px-4 pb-5 pt-2">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold">{t('composerTools.title')}</h2>
              <button type="button" onClick={() => setEditing(true)} className="inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-sm font-medium text-primary active:bg-primary/10">
                <SlidersHorizontal className="h-4 w-4" /> {t('composerTools.arrange')}
              </button>
            </div>
            {GROUPS.map((g) => (
              <section key={g.key} className="space-y-2.5">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t(g.titleKey)}</p>
                <ul className="grid grid-cols-4 gap-x-2 gap-y-3">
                  {COMPOSER_TOOLS.filter((tool) => tool.group === g.key).map((tool) => (
                    // empty:hidden — tiện ích chưa dùng được (vd chưa kết nối Evotech) không để lại ô trống.
                    <li key={tool.id} className="empty:hidden">
                      <ComposerToolNode id={tool.id} mode="tile" ctx={ctx} after={() => close(false)} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
            <p className="text-center text-[11px] text-muted-foreground">{t('composerTools.arrangeHint', { max: MAX_PINNED })}</p>
          </div>
        )}
      </BottomSheetContent>
    </BottomSheet>
  )
}

/** Chế độ "Sắp xếp": chọn tiện ích ghim + đổi thứ tự (kéo tay cầm hoặc mũi tên). Lưu ngay mỗi lần đổi. */
function ToolsEditor({ pinned, onChange, onDone }: { pinned: ComposerToolId[]; onChange: (next: ComposerToolId[]) => void; onDone: () => void }) {
  const { t } = useTranslation('convChat')
  const order = editorOrder(pinned)
  const drag = useDragReorder(pinned, (next) => onChange(next as ComposerToolId[]))
  const firstOff = order.findIndex((id) => !pinned.includes(id))

  const toggle = (id: ComposerToolId) => {
    const next = toggleTool(pinned, id)
    if (next === pinned) toast.info(t('composerTools.maxPinned', { max: MAX_PINNED }))
    else onChange(next)
  }

  return (
    <div className="pb-3 pt-2">
      <div className="flex items-center justify-between px-4 pb-1">
        <h2 className="text-base font-semibold">{t('composerTools.arrangeTitle')}</h2>
        <button
          type="button"
          onClick={() => onChange([...DEFAULT_PINNED])}
          disabled={isDefaultPinned(pinned)}
          className="inline-flex h-9 items-center gap-1 rounded-full px-2.5 text-sm text-muted-foreground active:bg-muted disabled:opacity-40"
        >
          <RotateCcw className="h-3.5 w-3.5" /> {t('composerTools.default')}
        </button>
      </div>
      <p className="px-4 pb-2 text-xs text-muted-foreground">
        {t('composerTools.editorHint', { n: pinned.length, max: MAX_PINNED })}
      </p>
      <ul ref={drag.listRef} className="relative">
        {order.map((id, i) => {
          const def = toolDef(id)!
          const Icon = def.icon
          const on = pinned.includes(id)
          const pos = pinned.indexOf(id)
          const dragging = drag.active?.id === id
          return (
            <li
              key={id}
              ref={drag.rowRef(id)}
              className={cn(dragging && 'relative z-10 bg-background shadow-lg ring-1 ring-primary/30')}
              style={dragging ? { transform: `translateY(${drag.active!.dy}px)` } : undefined}
            >
              {i === firstOff && firstOff > 0 && <p className="bg-muted/50 px-4 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{t('composerTools.inBox')}</p>}
              <div className="flex items-center gap-1 pr-2">
                {on ? (
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label={t('composerTools.dragToReorder', { label: def.label })}
                    {...drag.grip(id)}
                    style={{ touchAction: 'none' }}
                    className={cn('ml-2 flex h-11 w-9 shrink-0 cursor-grab items-center justify-center rounded-full text-muted-foreground active:cursor-grabbing', dragging && 'text-primary')}
                  >
                    <GripVertical className="h-5 w-5" />
                  </button>
                ) : <span className="ml-2 w-9 shrink-0" aria-hidden />}
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={on}
                  aria-label={t(on ? 'composerTools.unpinTool' : 'composerTools.pinTool', { label: def.label })}
                  onClick={() => toggle(id)}
                  className="flex min-h-[52px] min-w-0 flex-1 items-center gap-3 pr-2 text-left active:bg-muted"
                >
                  <span className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2', on ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40')}>
                    {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                  </span>
                  <Icon className="h-5 w-5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0">
                    <span className="block truncate text-[15px] font-medium">{def.label}</span>
                    <span className="block truncate text-xs text-muted-foreground">{def.hint}</span>
                  </span>
                </button>
                {on && (
                  <>
                    <button type="button" aria-label={t('composerTools.moveUp', { label: def.label })} disabled={pos === 0} onClick={() => onChange(moveTool(pinned, id, -1))} className="flex h-10 w-10 items-center justify-center rounded-full text-foreground active:bg-muted disabled:opacity-25">
                      <ArrowUp className="h-5 w-5" />
                    </button>
                    <button type="button" aria-label={t('composerTools.moveDown', { label: def.label })} disabled={pos === pinned.length - 1} onClick={() => onChange(moveTool(pinned, id, 1))} className="flex h-10 w-10 items-center justify-center rounded-full text-foreground active:bg-muted disabled:opacity-25">
                      <ArrowDown className="h-5 w-5" />
                    </button>
                  </>
                )}
              </div>
            </li>
          )
        })}
        {drag.active && drag.active.to !== drag.active.from && (
          <li aria-hidden data-testid="drop-line" className="pointer-events-none absolute inset-x-4 z-20 h-0.5 -translate-y-px rounded-full bg-primary" style={{ top: drag.active.lineY }} />
        )}
      </ul>
      <div className="px-4 pt-3">
        <button type="button" onClick={onDone} className="h-11 w-full rounded-full bg-primary text-[15px] font-medium text-primary-foreground active:opacity-90">{t('common:actions.done')}</button>
      </div>
    </div>
  )
}
